import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { ConfigService } from "./config.service";
import {EMPTY, Observable} from "rxjs";
import { SearchContext } from './shared/search-context';
import { AuthService } from '../auth/auth.service';
import { ConnectorsAuthenticators } from '../auth';
import { Microsoft } from '../auth/microsoft.service';

const AuthenticatorsObject: any = ConnectorsAuthenticators;

export interface AIProviderStatusResponse {
  status: string;
}

export interface ChatSession {
  id: number;
  owner: string;
  date_created: string;
  date_updated: string;
  subscribe: boolean;
  name: string;
  display_name?: string;
  active: boolean;
  tags: string[];
  ai_provider_id: number | null;
}

export interface PaginatedChatList {
  count: number;
  next: string | null;
  previous: string | null;
  results: ChatSession[];
}

export interface Message {
  id: number;
  chat_id: number;
  content: string;
  role: string;
}

@Injectable({
  providedIn: 'root'
})
export class SwirlService {

  private searchUrl: string;
  private paginationUrl: string;
  private authenticatorsUrl: string;
  private sourcesUrl: string;
  private brandingUrl: string;
  private messagesUrl: string;
  private chatsUrl: string;
  private chatsAIProvidersUrl: string;
  private ratingsUrl: string;

  // New Authenticator: Add to DI
  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private authService: AuthService,
    private microsoftAuth: Microsoft
  ) {
    this.searchUrl = '';
    this.paginationUrl = '';
    this.authenticatorsUrl = '/api/swirl/sapi/authenticators/';
    this.sourcesUrl = '/api/swirl/sapi/searchproviders/';
    this.brandingUrl = '/api/swirl/sapi/branding/';
    this.messagesUrl = '/api/swirl/sapi/messages/';
    this.chatsUrl = '/api/swirl/sapi/chats/';
    this.ratingsUrl = '/api/swirl/sapi/message-ratings';
    this.chatsAIProvidersUrl = '/api/swirl/sapi/is_chat_ai_provider_exists';
    configService.getConfig().subscribe(configResponse => {
      if (configResponse) {
        this.searchUrl = '/api/swirl/sapi/search/';
        this.paginationUrl = '/api/swirl/sapi/results/';
      }
    });
  }

  getSources(): Observable<any> {
    const csrfToken = this.authService.getCookie('csrftoken'); // get the CSRF token from the cookie
    let headers = new HttpHeaders()
      .set('X-CSRFToken', csrfToken) // include the CSRF token in the request headers
      .set('Authorization', 'Token ' + this.authService.getToken())
      .set('Content-Type', 'application/json');
    //TODO can we pass the params to GET?
    const response = this.http.get<SwirlResponse>(
      this.sourcesUrl,
      { headers }
    );
    return response;
  }

  getAuthenticators(): Observable<any> {
    const csrfToken = this.authService.getCookie('csrftoken'); // get the CSRF token from the cookie
    let headers = new HttpHeaders()
      .set('X-CSRFToken', csrfToken) // include the CSRF token in the request headers
      .set('Authorization', 'Token ' + this.authService.getToken())
      .set('Content-Type', 'application/json');
    //TODO can we pass the params to GET?
    const response = this.http.get<SwirlResponse>(
      this.authenticatorsUrl,
      { headers }
    );
    return response;
  }

  removeToken(idp: string): Observable<any> {
    const csrfToken = this.authService.getCookie('csrftoken'); // get the CSRF token from the cookie
    let headers = new HttpHeaders()
      .set('X-CSRFToken', csrfToken) // include the CSRF token in the request headers
      .set('Authorization', 'Token ' + this.authService.getToken())
      .set('Content-Type', 'application/json');
    //TODO can we pass the params to GET?
    const response = this.http.get<SwirlResponse>(
      `/api/swirl/sapi/authenticators/${idp.toLowerCase()}/remove_token`,
      { headers }
    );
    return response;
  }

  getBrandingValues(target: string): Observable<any> {
    const csrfToken = this.authService.getCookie('csrftoken'); // get the CSRF token from the cookie
    let headers = new HttpHeaders()
      .set('X-CSRFToken', csrfToken) // include the CSRF token in the request headers
      .set('Authorization', 'Token ' + this.authService.getToken())
      .set('Content-Type', 'application/json');
    
    const response = this.http.get<any>(
      `${this.brandingUrl}?target=${target}`,
      { headers }
    );
    
    return response;
  }

  getChats(page: number = 1, items: number = 10): Observable<PaginatedChatList> {
    const csrfToken = this.authService.getCookie('csrftoken');
    const headers = new HttpHeaders()
      .set('X-CSRFToken', csrfToken)
      .set('Authorization', 'Token ' + this.authService.getToken())
      .set('Content-Type', 'application/json');
  
    const params = new HttpParams()
      .set('page', page.toString())
      .set('items', items.toString());
  
    return this.http.get<PaginatedChatList>(this.chatsUrl, { headers, params });
  }

  getMessages(chatId: number, filter: boolean = true): Observable<Message[]> {
    const csrfToken = this.authService.getCookie('csrftoken');
    const headers = new HttpHeaders()
      .set('X-CSRFToken', csrfToken)
      .set('Authorization', 'Token ' + this.authService.getToken())
      .set('Content-Type', 'application/json');

    let params = new HttpParams().set('chat_id', chatId.toString());

    // If you want to leverage that 'filter' param from the backend for excluding system/tool messages:
    if (!filter) {
      params = params.set('filter', 'false');
    }

    // Now simply return an array of Message instead of a paginated object:
    return this.http.get<Message[]>(this.messagesUrl, { headers, params });
  }

  sendMessage(content: string, chatId?: number): Observable<any> {
    const csrfToken = this.authService.getCookie('csrftoken');
    let headers = new HttpHeaders()
      .set('X-CSRFToken', csrfToken)
      .set('Authorization', 'Token ' + this.authService.getToken())
      .set('Content-Type', 'application/json');

    let body = {};
    if (chatId)
      body = { content, role: 'user', chat_id: chatId };
    else
      body = { content, role: 'user' };
    return this.http.post<any>(this.messagesUrl, body, { headers });
  }

  createChat(): Observable<any> {
    const csrfToken = this.authService.getCookie('csrftoken');
    let headers = new HttpHeaders()
      .set('X-CSRFToken', csrfToken)
      .set('Authorization', 'Token ' + this.authService.getToken())
      .set('Content-Type', 'application/json');

    return this.http.post<any>(this.chatsUrl, {}, { headers });
  }

  getIsAIProviderExistsStatus(): Observable<AIProviderStatusResponse> {
    const csrfToken = this.authService.getCookie('csrftoken');
    let headers = new HttpHeaders()
      .set('X-CSRFToken', csrfToken)
      .set('Authorization', 'Token ' + this.authService.getToken())
      .set('Content-Type', 'application/json');

    return this.http.get<AIProviderStatusResponse>(this.chatsAIProvidersUrl, { headers });
  }

  deleteChat(chatId: number): Observable<any> {
    const csrfToken = this.authService.getCookie('csrftoken');
    const headers = new HttpHeaders()
      .set('X-CSRFToken', csrfToken)
      .set('Authorization', 'Token ' + this.authService.getToken())
      .set('Content-Type', 'application/json');
    // Assuming your base chatsUrl ends with a slash, we can append the chatId:
    const url = `${this.chatsUrl}${chatId}/`;
    return this.http.delete(url, { headers, observe: 'response' });
  }

  updateChatDisplayName(chatId: number, newDisplayName: string): Observable<any> {
    const csrfToken = this.authService.getCookie('csrftoken');
    const headers = new HttpHeaders()
      .set('X-CSRFToken', csrfToken)
      .set('Authorization', 'Token ' + this.authService.getToken())
      .set('Content-Type', 'application/json');
    const url = `${this.chatsUrl}${chatId}/`; // Ensure that chatsUrl ends with a slash if needed.
    // Build a payload with only the fields you want to update. Optionally include other properties if your backend requires them.
    const payload = { display_name: newDisplayName };
    return this.http.patch<any>(url, payload, { headers });
  }

  deleteAllChats(): Observable<any> {
    const csrfToken = this.authService.getCookie('csrftoken');
    const headers = new HttpHeaders()
      .set('X-CSRFToken', csrfToken)
      .set('Authorization', 'Token ' + this.authService.getToken())
      .set('Content-Type', 'application/json');
    // Construct the URL using the appropriate base; assuming you're using the "galaxy-chats" endpoint:
    const url = `${this.chatsUrl}destroy_all/`;
    return this.http.delete(url, { headers, observe: 'response' });
  }

  async search(searchContext: SearchContext): Promise<Observable<SwirlResponse>> {
    // Build URL parameters manually without using a custom encoder.
    const query = encodeURIComponent(searchContext.query);
    const rows = searchContext.rows.toString();
    const start = searchContext.start.toString();
    const page = searchContext.page;
    const searchId = searchContext.searchId;
    const refines = searchContext.refine;
    let providers = searchContext.providers;
    const filters = searchContext.filters;
    const connectorsNames = searchContext.connectorsNames;
    if (refines && refines.length > 0)
      providers = refines.map(r => r.value);

    let currentUrl: string;
    if (searchId === '') {
      currentUrl = this.searchUrl;
    } else {
      currentUrl = this.paginationUrl;
    }

    currentUrl += "?qs=" + query + "&format=json&search_id=" + searchId + "&page=" + page;
    if (providers.length > 0) {
      currentUrl += '&providers=' + providers.join(",");
    }
    if (filters.size > 0) {
      filters.forEach((value, key) => {
        const param = value.split(':');
        currentUrl += '&' + param[0] + '=' + param[1];
      });
    }

    const csrfToken = this.authService.getCookie('csrftoken'); // get the CSRF token from the cookie
    let headers = new HttpHeaders()
      .set('X-CSRFToken', csrfToken) // include the CSRF token in the request headers
      .set('Authorization', 'Token ' + this.authService.getToken())
      .set('Content-Type', 'application/json');
    
    // Add additional authentication headers if required by connectors.
    for (const connector of connectorsNames) {
      const isNeededAuthenticator = AuthenticatorsObject[connector];
      if (isNeededAuthenticator) {
        let authenticator;
        switch (AuthenticatorsObject[connector]) {
          case "Microsoft":
            authenticator = this.microsoftAuth;
            break;
          default:
            console.warn(`No Authenticator for ${connector}`);
        }
        if (authenticator) {
          let token = authenticator?.getToken();
          let isAuthenticated = authenticator?.isAuthenticated();
          if (!isAuthenticated) {
            token = await authenticator?.updateToken();
            isAuthenticated = authenticator?.isAuthenticated();
          }
          if (token && isAuthenticated) {
            headers = headers.set(`Authorization${authenticator?.name?.toLowerCase()}`, token);
          }
        }
      }
    }
    //TODO can we pass the params to GET?
    const response = this.http.get<SwirlResponse>(
      currentUrl,
      { headers }
    );
    return Promise.resolve(response);
  }

  rateResult(
      messageId: number,
      rating: 0 | 1,
      feedback?: string
  ): Observable<any> {
    const csrfToken = this.authService.getCookie('csrftoken');
    const headers = new HttpHeaders()
        .set('X-CSRFToken', csrfToken)
        .set('Authorization', 'Token ' + this.authService.getToken())
        .set('Content-Type', 'application/json');

    if (!messageId || messageId <= 0) {
      console.error('Cannot rate: invalid messageId', messageId);
      return EMPTY;
    }

    const body: any = {
      message_id: messageId,
      rating
    };

    if (feedback?.trim()) {
      body.feedback = feedback.trim();
    }

    const url = `${this.ratingsUrl}/`;
    return this.http.post<any>(url, body, { headers });
  }
}

export class SwirlResponse {
  messages?: Array<string>;
  info?: any; // Map<string, SwirlResultInfo>
  results?: Array<SwirlResult>;
}

export class SwirlResultInfo {
  [index: string]: any;
  retrieved?: number;
  filter_url?: string;
  search_time?: number;
  found_total?: number;
  retrieved_total?: number;
}

export class SwirlResult {
  [index: string]: any;
  swirl_rank?: number;
  swirl_score?: number;
  searchprovider?: string;
  searchprovider_rank?: number;
}
